import { createFileRoute } from '@tanstack/react-router';
import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';

function ManualQueue() {
  const utils = trpc.useUtils();
  const [tab, setTab] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const queue = trpc.billing.manual.queue.useQuery({ status: tab });

  const [opened, { open, close }] = useDisclosure(false);
  const [target, setTarget] = useState<number | null>(null);
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve');
  const [reason, setReason] = useState('');

  const review = trpc.billing.manual.review.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Review recorded', color: 'green' });
      void utils.billing.manual.queue.invalidate();
      void utils.billing.subscriptions.list.invalidate();
      close();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // Explicit shape — breaks tsc's deep recursion (TS2589) on the
  // Table + tRPC + Mantine intersection.
  interface ManualRow {
    id: number;
    vendorId: number;
    manualReference: string | null;
    proofUrl: string | null;
    requestedAt: string | Date;
    status: string;
    plan: { name: string } | null;
  }
  const rows = (queue.data ?? []) as readonly ManualRow[];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Manual subscriptions</Title>
          <SegmentedControl
            value={tab}
            onChange={(v) => setTab(v as 'pending' | 'approved' | 'rejected')}
            data={[
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
            ]}
          />
        </Group>

        <Card p={0}>
          {queue.isPending ? (
            <Skeleton height={250} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Vendor</Table.Th>
                  <Table.Th>Plan</Table.Th>
                  <Table.Th>Reference</Table.Th>
                  <Table.Th>Proof</Table.Th>
                  <Table.Th>Requested</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((m) => (
                  <Table.Tr key={m.id}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        #{m.vendorId}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{m.plan?.name ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {m.manualReference ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {m.proofUrl ? (
                        <Anchor href={m.proofUrl} target="_blank" size="sm">
                          view
                        </Anchor>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{new Date(m.requestedAt).toLocaleDateString()}</Text>
                    </Table.Td>
                    <Table.Td align="right">
                      {m.status === 'pending' ? (
                        <Group gap="xs" justify="flex-end">
                          <Button
                            size="xs"
                            color="green"
                            leftSection={<IconCheck size={14} />}
                            onClick={() => {
                              setTarget(m.id);
                              setDecision('approve');
                              setReason('');
                              open();
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="xs"
                            color="red"
                            variant="light"
                            leftSection={<IconX size={14} />}
                            onClick={() => {
                              setTarget(m.id);
                              setDecision('reject');
                              setReason('');
                              open();
                            }}
                          >
                            Reject
                          </Button>
                        </Group>
                      ) : (
                        <Badge variant="light" color={m.status === 'approved' ? 'green' : 'red'}>
                          {m.status}
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(queue.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">
                        Nothing in this bucket.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal
        opened={opened}
        onClose={close}
        title={decision === 'approve' ? 'Approve subscription' : 'Reject subscription'}
        centered
      >
        <Stack>
          <Text size="sm">
            Manual subscription request <strong>#{target}</strong>.
          </Text>
          {decision === 'approve' ? (
            <Text size="sm" c="dimmed">
              This will create an active subscription, set the period end based on the plan's
              interval, and unlock the vendor immediately.
            </Text>
          ) : (
            <Textarea
              label="Reason"
              placeholder="What should the vendor see when their request shows up as rejected?"
              minRows={3}
              autosize
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          )}
          <Group justify="end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button
              color={decision === 'approve' ? 'green' : 'red'}
              loading={review.isPending}
              onClick={() => {
                if (!target) return;
                review.mutate({
                  manualSubscriptionId: target,
                  decision,
                  rejectionReason: decision === 'reject' ? reason : undefined,
                });
              }}
            >
              {decision === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

export const Route = createFileRoute('/central/manual-subscriptions')({
  component: ManualQueue,
});
